import { BASE_URL } from 'config';
import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  Select,
  MenuItem,
  Button,
  Avatar,
  Typography,
  FormControl,
  FormHelperText,
  Grid,
  Divider,
  Input,
  IconButton,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import { validateEmail, validateRequired, validatePasswordMatch } from "validations";
import axios from "axios";
import Swal from "sweetalert2";
import avatar from "assets/theme/components/avatar";
import Autocomplete from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";
import { useMaterialUIController } from "context";
import { useTheme } from "@mui/material/styles";
const AddMarketsForm = ({ onClose, onSuccess, initialData }) => {
  console.log("🚀 ~ AddMarketsForm ~ initialData:", initialData);

  const [controller] = useMaterialUIController();
  const darkMode = controller.darkMode;
  const theme = useTheme();
  const [formData, setFormData] = useState({
    name: initialData?.name || "",
    is_active: initialData?.is_active || 1,
    id: initialData?.id || null,
    market_type_id: initialData?.market_type_id || null,
    country_id: initialData?.location_hierarchy?.country?.id || null,
    state_id: initialData?.location_hierarchy?.region?.id || null,
    city_id: initialData?.location_hierarchy?.province?.id || null,
  });

  // 1. عند تحميل initialData: عيّن الدولة فقط
  useEffect(() => {
    if (initialData) {
      setFormData((prev) => ({
        ...prev,
        name: initialData.name,
        is_active: initialData.is_active,
        id: initialData.id,
        market_type_id: initialData.market_type_id,
        country_id: initialData.location_hierarchy?.country?.id || null,
        state_id: initialData.location_hierarchy?.region?.id || null,
        city_id: initialData.location_hierarchy?.province?.id || null,
      }));
    }
  }, [initialData]);

  // 2. عند تغيير الدولة أو عند تحميل initialData، جلب الولايات
  useEffect(() => {
    const countryId = formData.country_id;
    if (!countryId) {
      setStates([]);
      setFormData((prev) => ({ ...prev, state_id: null, city_id: null }));
      setCities([]);
      return;
    }
    setLoadingStates(true);
    axios
      .get(`${BASE_URL}/administrator/locations/children/${countryId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("user-token")}` },
      })
      .then((res) => {
        setStates(res.data.data || []);
        // إذا كنا في وضع التعديل، عيّن الولاية التابعة للسوق
        if (initialData && initialData.location_hierarchy?.region?.id) {
          setFormData((prev) => ({
            ...prev,
            state_id: initialData.location_hierarchy.region.id,
          }));
        }
      })
      .catch(() => setStates([]))
      .finally(() => setLoadingStates(false));
    setFormData((prev) => ({ ...prev, state_id: null, city_id: null }));
    setCities([]);
  }, [formData.country_id, initialData]);

  // 3. عند تغيير الولاية أو عند تحميل initialData، جلب المدن
  useEffect(() => {
    const stateId = formData.state_id;
    if (!stateId) {
      setCities([]);
      setFormData((prev) => ({ ...prev, city_id: null }));
      return;
    }
    setLoadingCities(true);
    axios
      .get(`${BASE_URL}/administrator/locations/children/${stateId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("user-token")}` },
      })
      .then((res) => {
        setCities(res.data.data || []);
        // إذا كنا في وضع التعديل، عيّن المدينة التابعة للسوق
        if (initialData && initialData.location_hierarchy?.province?.id) {
          setFormData((prev) => ({
            ...prev,
            city_id: initialData.location_hierarchy.province.id,
          }));
        }
      })
      .catch(() => setCities([]))
      .finally(() => setLoadingCities(false));
    setFormData((prev) => ({ ...prev, city_id: null }));
  }, [formData.state_id, initialData]);
  const [errors, setErrors] = useState({});
  const [formSubmitted, setFormSubmitted] = useState(false);
  useEffect(() => {
    if (formSubmitted) validateForm();
  }, [formData]);

  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedState, setSelectedState] = useState(null);
  const [selectedCity, setSelectedCity] = useState(null);
  const [loadingCountries, setLoadingCountries] = useState(false);
  const [loadingStates, setLoadingStates] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);
  const [marketTypes, setMarketTypes] = useState([]);
  const [loadingMarketTypes, setLoadingMarketTypes] = useState(false);

  // جلب أنواع الأسواق
  useEffect(() => {
    setLoadingMarketTypes(true);
    axios
      .get(BASE_URL + "/administrator/market-types", {
        headers: { Authorization: `Bearer ${localStorage.getItem("user-token")}` },
      })
      .then((res) => {
        setMarketTypes(res.data.data || []);
      })
      .catch(() => setMarketTypes([]))
      .finally(() => setLoadingMarketTypes(false));
  }, []);

  // جلب الدول عند التحميل
  useEffect(() => {
    setLoadingCountries(true);
    axios
      .get(BASE_URL + "/administrator/locations/countries", {
        headers: { Authorization: `Bearer ${localStorage.getItem("user-token")}` },
      })
      .then((res) => {
        setCountries(res.data.data || []);
      })
      .catch(() => setCountries([]))
      .finally(() => setLoadingCountries(false));
  }, []);

  const validateForm = () => {
    const newErrors = {};
    if (!validateRequired(formData.name)) newErrors.name = "يجب إدخال الاسم";
    if (!formData.market_type_id) newErrors.market_type_id = "يرجى اختيار نوع السوق";
    if (!formData.country_id) newErrors.country = "يرجى اختيار الدولة";
    if (!formData.state_id) newErrors.state = "يرجى اختيار الولاية/المنطقة";
    if (!formData.city_id) newErrors.city = "يرجى اختيار المدينة";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };
  // create new market
  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormSubmitted(true);
    if (!validateForm()) return; // validate form before submission
    try {
      const formPayload = new FormData();
      // Append all form data including files
      Object.entries(formData).forEach(([key, value]) => {
        if (value !== null && value !== undefined && key !== "id") {
          // Handle avatar differently for existing vs new
          formPayload.append(key, value);
        }
      });
      // أضف location_id (رقم المدينة)
      formPayload.append("location_id", formData.city_id);
      // أرسل market_type_id
      formPayload.append("market_type_id", formData.market_type_id);
      // Show loading indicator
      Swal.fire({
        title: "جاري الحفظ...",
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
          const container = document.querySelector(".swal2-container");
          container.style.zIndex = "99999";
        },
      });
      console.log("Sending payload:", formPayload); // طباعة البيانات المرسلة
      const token = localStorage.getItem("user-token");
      const endpoint = formData.id
        ? `${BASE_URL}/administrator/markets/update/${formData.id}`
        : BASE_URL + "/administrator/markets/store";
      const response = await axios.post(endpoint, formPayload, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`,
        },
      });
      // Close loading indicator
      Swal.close();
      if (response.data.status) {
        await Swal.fire({
          icon: "success",
          title: "تم الحفظ!",
          text: "تم حفظ البيانات بنجاح",
          confirmButtonText: "موافق",
          didOpen: () => {
            const container = document.querySelector(".swal2-container");
            container.style.zIndex = "99999";
          },
        });
        onSuccess();
        onClose();
      } else {
        throw new Error(response.data.message || "حدث خطأ غير متوقع");
      }
    } catch (error) {
      Swal.close();
      console.error("Error Details:", error);
      const errorMessage = error.response?.data?.message || error.message || "حدث خطأ أثناء  الحفظ";
      Swal.fire({
        icon: "error",
        title: "فشل الحفظ!",
        text: errorMessage,
        confirmButtonText: "موافق",
        didOpen: () => {
          const container = document.querySelector(".swal2-container");
          container.style.zIndex = "99999";
        },
      });
    } finally {
      setFormSubmitted(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      name: "",
      is_active: 1,
    });
    setErrors({});
    onClose();
  };

  return (
    <Box
      sx={{
        p: 3,
        width: 600,
        bgcolor: darkMode ? theme.palette.background.default : "background.paper",
        borderRadius: 2,
        boxShadow: 3,
        color: darkMode ? "white !important" : "inherit",
      }}
    >
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" fontWeight="bold">
          {formData.id ? "تعديل السوق " : "إضافة  سوق جديد"}
        </Typography>
      </Box>

      {/* Main Form */}
      <Box container spacing={3}>
        {/* Left Column */}
        <Box display="flex" gap={2} sx={{ mb: 2 }}>
          <FormControl fullWidth error={!!errors.name} sx={{ mb: 2 }}>
            <TextField
              label="اسم  السوق *"
              name="name"
              value={formData.name}
              onChange={handleChange}
              error={!!errors.name}
              helperText={errors.name}
            />
          </FormControl>
          {/* حقل نوع السوق */}
          <FormControl fullWidth error={!!errors.market_type_id} sx={{ mb: 2 }}>
            <Autocomplete
              options={marketTypes}
              getOptionLabel={(option) => option.name || ""}
              loading={loadingMarketTypes}
              value={marketTypes.find((type) => type.id === formData.market_type_id) || ""}
              onChange={(_, newValue) =>
                setFormData((prev) => ({ ...prev, market_type_id: newValue ? newValue.id : null }))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="نوع السوق *"
                  error={!!errors.market_type_id}
                  helperText={errors.market_type_id}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingMarketTypes ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </FormControl>
          {/* حقل حالة النشاط */}
          <FormControl fullWidth sx={{ mb: 2 }}>
            <Select
              label="الحالة *"
              name="is_active"
              value={formData.is_active}
              onChange={handleChange}
              displayEmpty
              sx={{ height: "40px" }}
            >
              <MenuItem value="1">مفعل</MenuItem>
              <MenuItem value="0">غير مفعل</MenuItem>
            </Select>
          </FormControl>
        </Box>
        {/* صف اختيار الدولة/الولاية/المدينة */}
        <Box display="flex" gap={2} sx={{ mb: 2 }}>
          <FormControl fullWidth error={!!errors.country} sx={{ mb: 2 }}>
            <Autocomplete
              options={countries}
              getOptionLabel={(option) => option.name || ""}
              loading={loadingCountries}
              value={countries.find((c) => c.id === formData.country_id) || ""}
              onChange={(_, newValue) =>
                setFormData((prev) => ({ ...prev, country_id: newValue ? newValue.id : null }))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="الدولة *"
                  error={!!errors.country}
                  helperText={errors.country}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingCountries ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </FormControl>
          <FormControl fullWidth error={!!errors.state} sx={{ mb: 2 }}>
            <Autocomplete
              options={states}
              getOptionLabel={(option) => option.name || ""}
              loading={loadingStates}
              value={states.find((s) => s.id === formData.state_id) || ""}
              onChange={(_, newValue) =>
                setFormData((prev) => ({ ...prev, state_id: newValue ? newValue.id : null }))
              }
              disabled={!formData.country_id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="الولاية / المنطقة *"
                  error={!!errors.state}
                  helperText={errors.state}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingStates ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </FormControl>
          <FormControl fullWidth error={!!errors.city} sx={{ mb: 2 }}>
            <Autocomplete
              options={cities}
              getOptionLabel={(option) => option.name || ""}
              loading={loadingCities}
              value={cities.find((c) => c.id === formData.city_id) || ""}
              onChange={(_, newValue) =>
                setFormData((prev) => ({ ...prev, city_id: newValue ? newValue.id : null }))
              }
              disabled={!formData.state_id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="المدينة *"
                  error={!!errors.city}
                  helperText={errors.city}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingCities ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </FormControl>
        </Box>
      </Box>
      {/* Actions Section */}
      <Box mt={4} display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="body2" color="textSecondary">
          * الحقول المطلوبة
        </Typography>

        <Box display="flex" gap={2}>
          <Button variant="contained" color="error" onClick={handleCancel} sx={{ px: 4 }}>
            إلغاء
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            // disabled={Object.keys(errors).length > 0}
            sx={{ px: 4, color: "white !important" }}
            onClick={handleSubmit}
          >
            حفظ
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default AddMarketsForm;
